// ---------------------------------------------------------------------------
// The pie clock.
//
// A depleting disc, and the only timer in the game. Three things about it are
// deliberate rather than decorative:
//
//   1. There are no numerals inside the disc. The disc IS the readout, so the
//      seconds live in the accessible label instead, which is why `label` is
//      required rather than optional.
//   2. The urgent pulse is colour only. A clock that grows as it runs out is a
//      clock lying about its own geometry, and players read the wedge angle.
//      That rule binds the CALLER too, and one clock in the game used to break
//      it: letters selection spent TILE_FLIP_MS of the round clock per tile
//      called, so `remaining / duration` stepped fuller on every press, and the
//      screen had to stop the disc dead for each flip to keep the wedge honest.
//      Both are gone — the engine no longer extends that clock. If a phase ever
//      wants to hand time back again, the wedge is the constraint to design
//      against, and stopping the disc is the answer that was already tried and
//      already rejected for reading as a broken timer.
//   3. The frozen variant desaturates, greys the wedge and drops a pause glyph
//      into the centre. A player who mistakes a stopped clock for a running one
//      believes they were cheated, so it can never be a subtle difference.
//   4. Blue becomes red by crossfade, not by swap. A conic-gradient is not an
//      interpolable value, so the alert disc is a second layer stacked on the
//      calm one and its opacity is what moves. Everything else that carries the
//      variant — ticks, rim, the frozen desaturation — is a plain colour or a
//      shadow, so those transition directly. Entering crunch time is a state
//      change like any other and must not arrive as a jump.
// ---------------------------------------------------------------------------

import type { ReactElement } from 'react';
import { PauseGlyph } from './glyphs.js';

export type ClockVariant = 'normal' | 'alert' | 'buzz' | 'frozen';

export interface PieClockProps {
  /** 0 to 1. 1 is a full disc, 0 is empty. Clamped internally. */
  remaining: number;
  /** Pixel diameter. 220 | 200 | 128 | 96 | 64 | 56 are the sizes in the design. */
  size: number;
  variant?: ClockVariant;
  /** Six on a 30s clock, five on the 10s conundrum window. Defaults from size. */
  tickCount?: number;
  /** Accessible description, e.g. "18 seconds left". Required: the disc has no numerals. */
  label: string;
  className?: string;
}

interface TickGeometry {
  /** Distance from the centre to the tick, which sits outside the disc. */
  radius: number;
  barWidth: number;
  barHeight: number;
}

/** Straight from the handoff. Interpolating these looked wrong at 128 and 64. */
const TICK_GEOMETRY: Record<number, TickGeometry> = {
  220: { radius: 122, barWidth: 3, barHeight: 13 },
  200: { radius: 111, barWidth: 3, barHeight: 12 },
  128: { radius: 72, barWidth: 2, barHeight: 9 },
  96: { radius: 54, barWidth: 2, barHeight: 8 },
  64: { radius: 38, barWidth: 2, barHeight: 7 },
  56: { radius: 33, barWidth: 2, barHeight: 6 },
};

function tickGeometryFor(size: number): TickGeometry {
  const exact = TICK_GEOMETRY[size];
  if (exact) return exact;

  // An off-table size scales from its nearest neighbour rather than from a
  // formula, so the ticks stay just outside the rim at every size.
  const sizes = Object.keys(TICK_GEOMETRY).map(Number);
  const nearest = sizes.reduce((best, candidate) =>
    Math.abs(candidate - size) < Math.abs(best - size) ? candidate : best,
  );
  const base = TICK_GEOMETRY[nearest];
  const scale = size / nearest;
  return {
    radius: base.radius * scale,
    barWidth: Math.max(2, Math.round(base.barWidth * scale)),
    barHeight: Math.max(4, Math.round(base.barHeight * scale)),
  };
}

const WEDGE_COLOR: Record<ClockVariant, string> = {
  normal: 'var(--c-blue-400)',
  alert: 'var(--c-alert)',
  buzz: 'var(--c-accent)',
  frozen: 'var(--c-clock-frozen)',
};

const TICK_COLOR: Record<ClockVariant, string> = {
  normal: 'var(--c-clock-tick)',
  alert: 'var(--c-clock-tick-alert)',
  buzz: 'var(--c-clock-tick-accent)',
  frozen: 'var(--c-clock-frozen)',
};

const RIM_COLOR: Record<ClockVariant, string> = {
  normal: 'var(--c-clock-rim)',
  alert: 'var(--c-alert)',
  buzz: 'var(--c-accent)',
  frozen: 'var(--c-clock-frozen)',
};

export function PieClock({
  remaining,
  size,
  variant = 'normal',
  tickCount,
  label,
  className,
}: PieClockProps): ReactElement {
  const fraction = Number.isFinite(remaining) ? Math.min(1, Math.max(0, remaining)) : 0;
  const percent = fraction * 100;

  // The buzz window is ten seconds, so its ticks are worth two seconds each.
  const ticks = tickCount ?? (variant === 'buzz' ? 5 : 6);
  const { radius, barWidth, barHeight } = tickGeometryFor(size);
  const frozen = variant === 'frozen';
  const alert = variant === 'alert';

  // The alert disc always exists and only its opacity moves, so crunch time
  // fades in over the calm disc instead of replacing it. Mounting it on the
  // frame the clock turns urgent would give it nothing to transition from.
  const calm: ClockVariant = alert ? 'normal' : variant;
  const wedge = WEDGE_COLOR[calm];
  const glow = (color: string) => `0 10px 40px color-mix(in oklch, ${color} 30%, transparent)`;

  return (
    <div
      role="img"
      aria-label={label}
      data-clock-variant={variant}
      className={[
        'relative shrink-0 transition-[opacity,filter] duration-300 ease-out',
        alert ? 'clock-urgent' : '',
        frozen ? 'opacity-55 saturate-[0.3]' : '',
        className ?? '',
      ]
        .filter(Boolean)
        .join(' ')}
      style={{ width: size, height: size }}
    >
      {Array.from({ length: ticks }, (_, index) => (
        <div
          key={index}
          data-clock-tick
          className="absolute top-1/2 left-1/2 rounded-full transition-[background-color] duration-500 ease-out"
          style={{
            width: barWidth,
            height: barHeight,
            background: TICK_COLOR[variant],
            transform: `translate(-50%, -50%) rotate(${(index * 360) / ticks}deg) translateY(-${radius}px)`,
          }}
        />
      ))}

      <div
        className="absolute inset-0 rounded-full"
        style={{
          background: 'var(--c-clock-track)',
          boxShadow: 'inset 0 0 0 1px var(--c-clock-track-rim)',
        }}
      />

      <div
        data-clock-wedge
        className="absolute inset-0 rounded-full"
        style={{
          background: `conic-gradient(${wedge} 0 ${percent}%, transparent ${percent}% 100%)`,
          boxShadow: frozen ? undefined : glow(wedge),
        }}
      />

      <div
        data-clock-wedge-alert
        aria-hidden="true"
        className="absolute inset-0 rounded-full transition-opacity duration-500 ease-out"
        style={{
          background: `conic-gradient(${WEDGE_COLOR.alert} 0 ${percent}%, transparent ${percent}% 100%)`,
          boxShadow: glow(WEDGE_COLOR.alert),
          opacity: alert ? 1 : 0,
        }}
      />

      {/* Tagged so index.css can thicken it under reduced motion, where the
          urgent state has no pulse to carry it. */}
      <div
        data-clock-rim
        className="absolute inset-0 rounded-full transition-[box-shadow] duration-500 ease-out"
        style={{ boxShadow: `inset 0 0 0 2px ${RIM_COLOR[variant]}` }}
      />

      {frozen && (
        <div
          data-clock-paused
          className="absolute grid place-items-center rounded-full"
          style={{ inset: size * 0.125, background: 'var(--c-clock-track)' }}
        >
          <PauseGlyph size={size / 4} />
        </div>
      )}
    </div>
  );
}
